import React from 'react';
import { Box, CheckboxV2, Flex, TextV2 } from '@withjoy/joykit';
import { useDesignGalleryFiltersContext } from '../../../DesignGalleryFiltersProvider';
import type { FilterFacet, FilterFacetOption } from '../Filter.types';
import { useFilterChangeHandler } from '../useFilterChangeHandler';
import { colorSwatchBackground } from './colorSwatch';
import { FILTER_OPTION_LABEL_PROPS, FilterOptionLabel } from './FilterOptionLabel';
import { getCheckboxInputProps } from './checkboxInputProps';

type FiltersSidebarProps = Readonly<{
  /** The facet whose options the rail lists. Empty while the rail is closed. */
  facets: ReadonlyArray<FilterFacet>;
}>;

/**
 * Rail width. One column of checkboxes, a little narrower than the website designer's 300px
 * templates rail this is modelled on, so the column doesn't trail negative space (Arun,
 * 2026-10-16: "slightly (not much) narrower"). The longest option ("Destination (16)") still
 * fits on one line.
 */
export const FILTERS_SIDEBAR_WIDTH = 264;

/** `id` of the collapsing wrapper the gallery puts around the rail. */
export const FILTERS_SIDEBAR_ID = 'filters-sidebar';

/** A colour option's swatch, shared with the mobile drawer so both lists look alike. */
export const ColorSwatch = ({ background }: { background: string }) => (
  <Flex
    flex="none"
    width={5}
    height={5}
    borderRadius="100%"
    border="1px solid"
    borderColor="mono3"
    style={{ background }}
    aria-hidden="true"
    data-testid="filters-sidebar-swatch"
  />
);

const SidebarCheckboxRow = ({
  option,
  facetGroupCode,
  isChecked,
  onChange,
}: {
  option: FilterFacetOption;
  facetGroupCode: string;
  isChecked: boolean;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
}) => {
  const swatch = colorSwatchBackground(option.compositeCode);
  return (
    <CheckboxV2
      isChecked={isChecked}
      onChange={onChange}
      overrides={{
        Root: { props: { width: '100%', paddingY: 2 } },
        Label: { props: { typographyVariant: 'body1', ...FILTER_OPTION_LABEL_PROPS } },
        // No `Control` overrides. This is a deliberate visual change, not a restatement of the
        // defaults: the old `mono3` fill with a dark check in both states becomes CheckboxV2's
        // unfilled `mono5`-bordered box, and a solid `mono14` fill with a white check once
        // ticked. The dropped `_focus: { boxShadow: 'none' }` only ever removed the resting
        // shadow while focused — the focus ring is an `outline` on `_focusVisible`, so it was
        // never suppressed. Tracked in doc/plans/remove-checkbox-color-overrides.md; needs a
        // design eye before it ships.
        Input: { props: getCheckboxInputProps(option.compositeCode, 'sidebar', facetGroupCode) },
      }}
    >
      <FilterOptionLabel
        label={option.label}
        count={option.matchingItemCount}
        typographyVariant="body1"
        swatch={swatch && <ColorSwatch background={swatch} />}
      />
    </CheckboxV2>
  );
};

/**
 * The filter rail: one facet at a time, every one of its options in a single column under the
 * facet's own name (Jeff, 2026-10-13: "clicking the pill opens the sidebar, but with only that
 * 1 facet… 3 separate sidebars, only one visible at a time"). The pills above carry the
 * selection, so the rail holds no chips and no reset, and the pill that opened it closes it.
 *
 * The rail scrolls with the page rather than on its own, and its heading's line box starts at
 * the rail's top edge so it reads level with the first tile. Desktop only — mobile swaps the
 * pills for a single drawer trigger, so no rail is rendered there.
 */
export const FiltersSidebar = ({ facets }: FiltersSidebarProps) => {
  const { activePrintFilters } = useDesignGalleryFiltersContext();
  const handleOnFilterChange = useFilterChangeHandler();
  const facet = facets[0];

  if (!facet) return null;

  // Counts are scoped to the other facets' selections, so a selected option can read 0; it
  // stays listed so it can be unchecked.
  const presentable = facet.options.filter(option => option.matchingItemCount > 0 || activePrintFilters.has(option.compositeCode));

  return (
    <Box
      as="aside"
      aria-label={facet.label}
      data-testid="filters-sidebar"
      flex="none"
      width={FILTERS_SIDEBAR_WIDTH}
      alignSelf="flex-start"
      paddingRight={4}
    >
      <Flex
        alignItems="flex-start"
        marginBottom={3}
        data-testid={`filters-sidebar-section-${facet.groupCode}`}
      >
        <TextV2
          as="h3"
          typographyVariant="hed1"
          color="mono14"
        >
          {facet.label}
        </TextV2>
      </Flex>
      <Flex flexDirection="column">
        {presentable.map(option => (
          <SidebarCheckboxRow
            key={option.compositeCode}
            option={option}
            facetGroupCode={facet.groupCode}
            isChecked={activePrintFilters.has(option.compositeCode)}
            onChange={handleOnFilterChange}
          />
        ))}
      </Flex>
    </Box>
  );
};
